import React, { useMemo, useState } from 'react';
import { useGetMyAppointmentsQuery, useCancelAppointmentMutation } from '../../services/api/kinesioApi.js';
import { toast } from '../../components/ui/use-toast';
import { Calendar, Clock, Stethoscope, CheckCircle2, XCircle, Clock4, ArrowLeft, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es';
import { AnimatePresence, motion } from 'framer-motion';
import PublicNavbar from '../nav/PublicNavbar.jsx';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

const MyAppointments = () => {
    const { data: appointments, isLoading, error } = useGetMyAppointmentsQuery();
    const [cancelAppointment] = useCancelAppointmentMutation();
    const navigate = useNavigate();
    const [cancellingId, setCancellingId] = useState(null);
    const [appointmentToCancel, setAppointmentToCancel] = useState(null);

    const confirmCancel = async () => {
        if (!appointmentToCancel) return;
        const id = appointmentToCancel;
        setAppointmentToCancel(null);
        setCancellingId(id);
        try {
            await cancelAppointment({ id, cancel_reason: 'ausencia_paciente' }).unwrap();
            toast({ title: 'Turno cancelado', description: 'El turno ha sido cancelado exitosamente.' });
        } catch (err) {
            toast({ title: 'Error', description: err?.data?.error || 'No se pudo cancelar el turno.', variant: 'destructive' });
        } finally {
            setCancellingId(null);
        }
    };

    const { upcoming, past } = useMemo(() => {
        if (!appointments) return { upcoming: [], past: [] };
        const now = dayjs().tz('America/Argentina/Buenos_Aires');
        const upcomingList = [];
        const pastList = [];

        appointments.forEach(appt => {
            const apptDate = dayjs(appt.fecha_hora).tz('America/Argentina/Buenos_Aires');
            if (apptDate.isAfter(now) && appt.estado !== 'cancelado') {
                upcomingList.push(appt);
            } else {
                pastList.push(appt);
            }
        });

        return { 
            upcoming: upcomingList.sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora)), 
            past: pastList.sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora)) 
        };
    }, [appointments]);

    const getStatusStyle = (status) => {
        switch(status) {
            case 'confirmado':
            case 'completado': return 'bg-green-100 text-green-700 border-green-200';
            case 'cancelado': return 'bg-red-100 text-red-700 border-red-200';
            case 'pendiente_pago': return 'bg-orange-100 text-orange-700 border-orange-200';
            default: return 'bg-blue-100 text-blue-700 border-blue-200';
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'confirmado':
            case 'completado': return <CheckCircle2 size={14} />;
            case 'cancelado': return <XCircle size={14} />;
            default: return <Clock4 size={14} />;
        }
    };

    return (
        <div className="bg-[#f7f9fc] min-h-screen font-sans text-gray-800">
            <PublicNavbar />
            <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
                
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <button onClick={() => navigate('/reservar')} className="p-2.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50 text-gray-600 transition-colors shadow-sm">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1E293B]">Mis Turnos</h1>
                        <p className="text-gray-500 text-sm mt-1">Revisa el historial de tus turnos programados y pasados.</p>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0a47d4] mb-4"></div>
                        <p>Cargando tus turnos...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-2xl text-center">
                        <p className="font-semibold">No se pudieron cargar tus turnos.</p>
                        <p className="text-sm mt-1">Por favor, asegúrate de haber iniciado sesión y vuelve a intentar.</p>
                        <button onClick={() => navigate('/login')} className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg font-bold text-sm">Iniciar Sesión</button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-10">
                        
                        {/* Próximos Turnos */}
                        <section>
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Calendar size={22} className="text-[#0a47d4]" />
                                Próximos Turnos
                            </h2>
                            {upcoming.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-8 text-center flex flex-col items-center">
                                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-400 mb-3">
                                        <Calendar size={32} />
                                    </div>
                                    <p className="text-gray-500 font-medium">No tienes turnos programados a futuro.</p>
                                    <button onClick={() => navigate('/reservar')} className="mt-4 text-[#0a47d4] font-bold hover:underline">Reservar un nuevo turno</button>
                                </div>
                            ) : (
                                <div className="grid gap-4">
                                    {upcoming.map(appt => (
                                        <AppointmentCard key={appt.id} appt={appt} getStatusStyle={getStatusStyle} getStatusIcon={getStatusIcon} handleCancel={setAppointmentToCancel} isCancelling={cancellingId === appt.id} isUpcoming={true} />
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Historial (Pasados o Cancelados) */}
                        <section>
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Clock size={22} className="text-gray-400" />
                                Historial
                            </h2>
                            {past.length === 0 ? (
                                <p className="text-gray-500 italic text-sm">Aún no tienes un historial de turnos previos.</p>
                            ) : (
                                <div className="grid gap-4 opacity-80">
                                    {past.map(appt => (
                                        <AppointmentCard key={appt.id} appt={appt} getStatusStyle={getStatusStyle} getStatusIcon={getStatusIcon} />
                                    ))}
                                </div>
                            )}
                        </section>

                    </div>
                )}
            </div>

            <AnimatePresence>
                {appointmentToCancel && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
                            className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-md shadow-2xl relative"
                        >
                            <button
                                onClick={() => setAppointmentToCancel(null)}
                                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                            
                            <div className="flex flex-col items-center text-center mt-2">
                                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
                                    <XCircle size={32} strokeWidth={2} />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">¿Cancelar Turno?</h3>
                                <p className="text-gray-600 mb-8 text-base">
                                    Estás a punto de cancelar tu turno. Esta acción no se puede deshacer y el horario volverá a quedar disponible para otros pacientes.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-3 w-full">
                                    <button
                                        onClick={() => setAppointmentToCancel(null)}
                                        className="flex-1 px-4 py-3 rounded-xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                                    >
                                        Mantener Turno
                                    </button>
                                    <button
                                        onClick={confirmCancel}
                                        className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-red-600 hover:bg-red-700 transition-colors flex justify-center items-center gap-2"
                                    >
                                        Sí, Cancelar
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

const AppointmentCard = ({ appt, getStatusStyle, getStatusIcon, handleCancel, isCancelling, isUpcoming }) => {
    const date = dayjs(appt.fecha_hora).tz('America/Argentina/Buenos_Aires');
    return (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col sm:flex-row items-start sm:items-center gap-5 transition-transform hover:-translate-y-0.5">
            <div className="flex flex-col items-center justify-center bg-blue-50 border border-blue-100 text-blue-800 rounded-xl w-16 h-16 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-wider">{date.format('MMM')}</span>
                <span className="text-2xl font-black leading-none">{date.format('DD')}</span>
            </div>
            
            <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-1.5">
                    <h3 className="font-bold text-gray-900 text-lg capitalize">{date.format('dddd, YYYY')}</h3>
                    <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 border ${getStatusStyle(appt.estado)}`}>
                        {getStatusIcon(appt.estado)}
                        {appt.estado || 'Pendiente'}
                    </span>
                </div>
                
                <div className="flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-gray-600 font-medium">
                    <span className="flex items-center gap-1.5">
                        <Clock size={16} className="text-gray-400" />
                        {date.format('HH:mm')} hs
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Stethoscope size={16} className="text-gray-400" />
                        {appt.motivo || 'Kinesiología'}
                    </span>
                    {appt.professional && (
                        <span className="flex items-center gap-1.5">
                            <span className="text-gray-400">Prof:</span>
                            {appt.professional.first_name || appt.professional.firstName} {appt.professional.last_name || appt.professional.lastName}
                        </span>
                    )}
                </div>
            </div>
            
            {isUpcoming && appt.estado !== 'cancelado' && (
                <div className="shrink-0 sm:ml-4 flex items-center justify-end w-full sm:w-auto pt-4 sm:pt-0 border-t border-gray-100 sm:border-0 mt-2 sm:mt-0">
                    <button 
                        onClick={() => handleCancel && handleCancel(appt.id)}
                        disabled={isCancelling}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50 w-full sm:w-auto justify-center"
                    >
                        {isCancelling ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                        Cancelar Turno
                    </button>
                </div>
            )}
        </div>
    );
};

export default MyAppointments;
